import React, {useEffect, useState, useCallback} from 'react';
import styled, {css} from 'styled-components';
import {useNavigate, useParams} from 'react-router-dom';

import {ToastContainer, toast, Slide} from 'react-toastify';
import '../../themes/toastify/main.scss';

import {
  BoxedDropdown,
  dimensions,
  ModuleDetailProps,
  ModuleHeading,
  OptionItem,
  OptionList,
  TileImageItem
} from '@bettermarks/bm-ui-components';

import {
  Content,
  Group,
  Layout,
  LayoutComponentService
} from '../../components/LayoutComponents';
import {useLibrary} from '../../services/library.service';
import {Module, ModuleContext, ModuleChapter} from '../../types';

import {Modal} from '../../components/modal/Modal';
import {Overlay, useOverlay} from '../../components/Overlay';
import {ModuleBar} from './ModuleBar';
import libraryMappings from '../../data/libraries/color-and-icon-mappings.json';
import {useRoutes} from '../../services/route.service';
import {usePlanning} from '../../services/planning.service';
import {useStorage} from '../../services/pt-storage.service';
import {CheckBox} from '../../components/Checkbox';

import useWindowDimensions from '../../helpers/useWindowDimensions';

import {registerLocale} from 'react-datepicker';
import {setHours, setMinutes, format} from 'date-fns';
import de from 'date-fns/locale/de';
registerLocale('de', de);

import classData from '../../data/classes/classData.json';
import {findIndex} from 'lodash';
import {ClassListAndStudents} from './ClassListAndStudents';
import {BookIntro, HeadingGroup, StyledModuleHeading} from './BookIntro';
import {Calendars} from './Calendars';
import {BookHeader} from './BookHeader';
import {Lob} from '../../components/Lob/Lob';
import AllocateToolbar from '../../components/AllocateToolbar/AllocateToolbar';
import {AllocationModal} from '../../components/modal/AllocationModal';

export const CheckboxItem = styled(OptionItem)``;

export const CustomOptionList = styled(OptionList)`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-width: 1px;

  [class*='components__HeaderWrapper'] {
    [class*='OptionList__OptionItem'] {
      border-bottom: 0;
    }
  }
  [class*='OptionList__OptionItem'] {
    border-width: 1px;
    padding: 0;
  }
  [class*='components__List'] {
    height: 308px;
  }
  [class^='OptionList-sc'] {
    max-height: initial;
  }
`;

export const StyledBoxedDropdown = styled(BoxedDropdown)`
  label {
    display: none;
  }
  input {
    padding: 0 0 0 12px;
  }
  [class^='BoxedInput__SelectBox'] {
    border-left: none;
  }
  [class^='Dropdown__DropdownContent'] {
    top: 52px;
  }
  [class^='OptionList__OptionItem'] {
    min-height: 40px;
  }
  [class^='BoxedInput__BoxedInputWrapper'] {
    height: 40px;
  }
`;

const AllocateOverlay = styled.div<{active?: boolean}>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  background: rgba(0, 0, 0, 0.8);

  ${p =>
    p.active
      ? css`
          visibility: visible;
          opacity: 1;
          transition: all 1s ease-in-out;
        `
      : css`
          visibility: hidden;
          opacity: 0;
          transition: all 600ms ease-out;
        `};
`;

export const ClassAndStudents = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${CheckBox} > span {
    margin: 0 12px;
    flex: 0 0 16px;
  }
  ${CheckBox} > span + div {
    flex: 1 1 auto;
  }
`;

export const ModulePage: React.FC<{context: ModuleContext}> = ({context}) => {
  const [chapterIntro, setModuleIntro] = useState<ModuleDetailProps>({
    preknowledge: [],
    objectives: [],
    examples: []
  });

  const OverlayService = useOverlay(),
    RouteService = useRoutes(),
    LibraryService = useLibrary(),
    PlanningService = usePlanning(),
    navigate = useNavigate(),
    StorageService = useStorage();

  const [module, setModule] = useState<Module>();
  const bookTitle = module?.name;
  const [modalConfig, setModalConfig] = useState<{
    isOpen: boolean;
    modalTitle?: any;
    title?: string;
    previewImages?: TileImageItem[];
    meta?: string;
    contentTasks?: any[];
    fems?: any;
    caption: string;
  }>({
    title: '',
    modalTitle: 'ExerciseList',
    isOpen: false,
    previewImages: [],
    meta: '',
    contentTasks: [],
    fems: [],
    caption: ''
  });

  const [moduleIconAndColor, setIconColor] = useState<{
    color: string;
    icon: string;
  }>({
    color: '',
    icon: ''
  });

  const {libraryId, moduleId} = {
    libraryId: '',
    moduleId: '',
    ...useParams<{libraryId: string; moduleId: string}>()
  };
  const [libraryName, setLibraryName] = useState<string>('');

  const updateModuleBar = () =>
    (LayoutComponentService.header = [
      <ModuleBar
        libraryId={libraryId}
        libraryName={LibraryService.getLibraryName(libraryId)}
        context={context}
        key={'module-bar'}
        onAddToPlanning={addToPlanning}
        onRemoveFromPlanning={removeFromPlanning}
        isPlanned={PlanningService.itemIsPlanned(moduleId)}
        module={module?.name}
      />
    ]);

  const addToPlanning = () => {
    PlanningService.add(moduleId, libraryId, moduleIconAndColor.color);
    updateModuleBar();
  };

  const removeFromPlanning = () => {
    navigate('/planning', {state: {removeFromPlanning: moduleId}});
  };

  useEffect(() => {
    setLibraryName(LibraryService.getLibraryName(libraryId));
    const m = LibraryService.getModule(moduleId);
    LibraryService.library$(StorageService.getVersion(), libraryId).subscribe(
      (library: any) => {
        const domain = LibraryService.getDomainForModule(library, m);
        const icon = (
          libraryMappings.books.find(b => b.id === moduleId) || {
            bookIcon: 'capital-X.svg'
          }
        ).bookIcon;
        setIconColor({color: domain?.color || '#666', icon});
      }
    );
    setModuleIntro(LibraryService.chapterIntro(moduleId));
    setModule(m);

    RouteService.setContentRoute(RouteService.libraryRoute(libraryId));
    updateModuleBar();
  }, [moduleId]);

  const closeModal = () => {
    setModalConfig({isOpen: false, caption: ''});
    OverlayService.deactivate();
    setActiveLob('');
  };

  const openLobDetails = (
    title: string,
    modalTitle: any,
    previewImages: TileImageItem[],
    meta: string,
    contentTasks: any[],
    caption: string
  ): void => {
    //OverlayService.fadeIn(() => closeModal());
    setModalConfig({
      title,
      modalTitle,
      previewImages,
      meta,
      contentTasks,
      isOpen: true,
      caption
    });
  };

  const openChapterPreview = (
    title: string,
    modalTitle: any,
    contentListRef: string,
    meta: string,
    contentTasks: any[],
    caption: string
  ) => {
    openLobDetails(
      title,
      modalTitle,
      LibraryService.getPreviewExerciseList(contentListRef),
      meta,
      contentTasks,
      caption
    );
  };

  const [activeLob, setActiveLob] = useState<string>('');

  const handleActiveLob = (
    e: string,
    title: string,
    modalTitle: any,
    contentListRef: string,
    meta: string,
    contentTasks: any[],
    caption: string
  ) => {
    activeLob !== e ? setActiveLob(e) : null;

    openChapterPreview(
      title,
      modalTitle,
      contentListRef,
      meta,
      contentTasks,
      caption
    );
  };

  const [checkedItemIds, setCheckedItemIds] = useState<string[]>([]);

  const [lobList, setLobList] = useState<string[]>([]);

  function handleCheckLob(itemId: string) {
    if (checkedItemIds.indexOf(itemId) > -1) {
      setCheckedItemIds(prev => prev.filter(i => i !== itemId));
    } else {
      setCheckedItemIds(prev => [...prev, itemId]);
    }
  }

  const updateLobList = (id: string) => {
    if (checkedItemIds.includes(id)) {
      setLobList(lobList.filter(tool => tool != id));
    } else {
      setLobList(lobList => [...lobList, id]);
    }
  };

  const [openAllocate, setOpenAllocate] = useState(false);

  const close = () => setOpenAllocate(!openAllocate);

  const resetAllocation = () => {
    setCheckedItemIds([]);
    setLobList([]);
    setOpenAllocate(false);
  };

  const {position} = useWindowDimensions(1400);
  const getCheckedItems = (chapter: ModuleChapter) => {
    for (let i = 0; i < chapter?.chapters?.length; i++) {
      if (checkedItemIds.indexOf(chapter?.chapters[i].name) === -1)
        return false;
    }
    return true;
  };
  const removeCheckedItem = (chapter: ModuleChapter) => {
    const _checkedItemIds = [...checkedItemIds];
    for (let i = 0; i < chapter?.chapters?.length; i++) {
      const index = _checkedItemIds.indexOf(chapter.chapters[i].name);
      if (index > -1) {
        _checkedItemIds.splice(index, 1);
      }
    }
    return _checkedItemIds;
  };
  function handleCheckChapter(chapter: ModuleChapter) {
    if (getCheckedItems(chapter)) {
      setCheckedItemIds(removeCheckedItem(chapter));
    } else {
      setCheckedItemIds([
        ...new Set([
          ...chapter.chapters.map((chapter: any) => chapter.name),
          ...checkedItemIds
        ])
      ]);
    }
  }

  // Date Stuff
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minH = setHours(setMinutes(today, 0), 0);
  const maxH = setHours(setMinutes(tomorrow, 59), 23);
  const [startDate, setStartDate] = useState(minH);
  const [endDate, setEndDate] = useState(maxH);

  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const setCurrentDate = (date: Date) => {
    if (isToday(date)) {
      return 'Heute';
    } else {
      return date.toLocaleDateString('de-DE', {
        month: 'numeric',
        day: 'numeric'
      });
    }
  };

  const formatedDateToolbar =
    isToday(startDate) && isToday(endDate)
      ? setCurrentDate(startDate)
      : setCurrentDate(startDate) +
        ' – ' +
        endDate.toLocaleDateString('de-DE', {
          month: 'numeric',
          day: 'numeric'
        });

  const formatedDateModal =
    `${startDate.toLocaleDateString('de-DE', {
      month: 'numeric',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric'
    })}` +
    ` – ` +
    `${endDate.toLocaleDateString('de-DE', {
      month: 'numeric',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric'
    })}`;

  const calendars = Calendars(
    today,
    startDate,
    setStartDate,
    endDate,
    setEndDate
  );

  // classes

  const selClass1 = classData[0].students;
  const selClass2 = classData[1].students;
  const selClass3 = classData[2].students;

  const [allSelected, setAllSelected] = React.useState(true);

  const [selection, setSelection] = useState<string>(classData[0].label);

  const currentSelectedClass =
    selection === 'Klasse 5a'
      ? selClass1
      : selection === 'Klasse 6b'
      ? selClass2
      : selClass3;

  const [items, setItems] = React.useState<any[]>(selClass1);

  const savedList = Object.values(items).filter(
    item => item.active === true
  ).length;

  const store = Object.values(items).filter(item => item.active === true);

  const selectedClassAndStudents =
    selection +
    ' (' +
    (savedList === items.length
      ? 'alle SuS'
      : savedList === 0
      ? 'keine SuS'
      : savedList > 0 && savedList + ' SuS') +
    ')';

  const selectedStudentsCheckBox =
    items.length > savedList
      ? 'Ganze Klasse auswählen'
      : 'Ganze Klasse abwählen';

  const [storeSelection, setStoreSelection] = useState<any[]>([
    {class: selection},
    {sutdents: store}
  ]);

  React.useEffect(() => {
    setAllSelected(items.every((i: {active: any}) => i.active));
  }, [items]);

  React.useEffect(() => {
    setSelection(selection);
    setItems(currentSelectedClass);
    setStoreSelection(storeSelection);
  }, [selection]);

  const select = (item: any) => {
    const index = findIndex(items, i => i.label === item.label);
    const newItem = {...item, active: !item.active};
    setItems([...items.slice(0, index), newItem, ...items.slice(index + 1)]);
  };

  const toggleAll = () => {
    setItems(items.map((i: any) => ({...i, active: !allSelected})));
  };

  // classes

  const createAssignment = () => {
    toast.success(
      'Sie haben die ausgewählten Lernziele erfolgreich zugewiesen.'
    );
    close();
    resetAllocation();
  };

  return (
    <>
      {module && (
        <Layout
          customToolbar={
            <AllocateToolbar
              count={checkedItemIds.length}
              selectedDate={formatedDateToolbar}
              selectedStudents={selectedClassAndStudents}
              allocate={() => {
                setOpenAllocate(true);
                closeModal();
              }}
              createAssignment={createAssignment}
              deselect={resetAllocation}
              disabledButton={savedList < 1}
            />
          }
        >
          <Content>
            {BookHeader(moduleIconAndColor, module, chapterIntro)}

            <Group>
              {module?.chapters?.slice(0, 2).map(chapter => (
                <Group key={chapter.id}>
                  <StyledModuleHeading
                    as='h4'
                    border
                    gap={chapter?.chapters?.length < 2}
                  >
                    {chapter?.chapters?.length > 1 && (
                      <CheckBox
                        checked={getCheckedItems(chapter)}
                        onClick={() => handleCheckChapter(chapter)}
                      />
                    )}

                    <HeadingGroup>
                      {chapter.caption} {chapter.name}
                    </HeadingGroup>
                  </StyledModuleHeading>

                  {chapter?.chapters?.map(item => (
                    <Lob
                      key={item.id}
                      caption={item.caption}
                      title={item.name}
                      checked={checkedItemIds.indexOf(item.name) > -1}
                      active={activeLob === item.name}
                      activeIndex={activeLob}
                      changeHandler={() => {
                        handleCheckLob(item.name);
                        updateLobList(item.name);
                      }}
                      meta={item.meta}
                      actionOpenDetails={() => {
                        handleActiveLob(
                          item.name,
                          item.name,
                          item.chapters[0].content_type,
                          item.chapters[0].content_list_ref,
                          item.meta,
                          item.chapters,
                          item.caption
                        );
                        console.log('fems', item.chapters[0].fems);
                      }}
                      activityType={item.chapters[0].content_type}
                      contentTasks={item.chapters}
                    />
                  ))}
                </Group>
              ))}
            </Group>

            {BookIntro}

            <Group>
              {module?.chapters?.slice(2, -1).map(chapter => (
                <Group key={chapter.id}>
                  <StyledModuleHeading
                    as='h4'
                    border
                    gap={chapter?.chapters?.length < 2}
                  >
                    {chapter?.chapters?.length > 1 && (
                      <CheckBox
                        checked={getCheckedItems(chapter)}
                        onClick={() => handleCheckChapter(chapter)}
                      />
                    )}
                    <HeadingGroup>
                      {chapter.caption} {chapter.name}
                    </HeadingGroup>
                  </StyledModuleHeading>

                  {chapter?.chapters?.map(item => (
                    <Lob
                      key={item.id}
                      caption={item.caption}
                      title={item.name}
                      checked={checkedItemIds.indexOf(item.name) > -1}
                      active={activeLob === item.name}
                      activeIndex={activeLob}
                      changeHandler={() => {
                        handleCheckLob(item.name);
                        updateLobList(item.name);
                      }}
                      meta={item.meta}
                      actionOpenDetails={() => {
                        handleActiveLob(
                          item.name,
                          item.name,
                          item.chapters[0].content_type,
                          item.chapters[0].content_list_ref,
                          item.meta,
                          item.chapters,
                          item.caption
                        );
                        console.log('fems', item.chapters[0].fems);
                      }}
                      activityType={item.chapters[0].content_type}
                      contentTasks={item.chapters}
                    />
                  ))}
                </Group>
              ))}
            </Group>

            <Modal
              color={moduleIconAndColor.color}
              position={position}
              caption={modalConfig.caption}
              modalTitle={modalConfig.modalTitle}
              title={modalConfig.title || ''}
              examples={modalConfig.previewImages}
              modalOpen={modalConfig.isOpen}
              closeModal={closeModal}
              meta={modalConfig.meta}
              contentTasks={modalConfig.contentTasks}
              asDetailTooltip
              onStart={() => {
                alert('Aufgabenserie starten');
              }}
            />
          </Content>

          <AllocationModal
            calendars={calendars}
            selectedClassAndStudents={selectedClassAndStudents}
            modalOpen={openAllocate}
            title={formatedDateModal}
            modalTitle='To-dos erstellen und anpassen'
            closeModal={close}
            lobList={checkedItemIds}
            classAndStudents={ClassListAndStudents(
              classData,
              selection,
              setSelection,
              items,
              allSelected,
              toggleAll,
              selectedStudentsCheckBox,
              select
            )}
            createAssignment={createAssignment}
            onRemoveMiniLob={() =>
              alert('diese Funktion steht noch nicht zur Verfügung!')
            }
            disabledButton={savedList < 1}
          />
          <ToastContainer
            position='top-right'
            transition={Slide}
            hideProgressBar={true}
            autoClose={3000}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
          <Overlay />
          <AllocateOverlay active={openAllocate} />
        </Layout>
      )}
    </>
  );
};
